import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { COUNTRY_CHOICES } from '@/constants';
import { validateEmail } from '@/auth/utils';
import { CountryCode } from '@/types';
import { useAuth } from '@/auth/context';
import { useTranslation } from 'react-i18next';

type PublicPlanKind = 'free' | 'standard' | 'premium';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { signInWithGoogleFree } = useAuth();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const { t: tErr } = useTranslation('errors');
  const selectedPlan = useMemo<PublicPlanKind>(() => {
    const raw = searchParams.get('plan');
    return raw === 'standard' || raw === 'premium' ? raw : 'free';
  }, [searchParams]);

  const planCopy = useMemo(() => ({
    free: {
      eyebrow: t('freeAccessEyebrow'),
      title: t('startFreeAccessTitle'),
      description: t('freeAccessDesc'),
      submitLabel: t('startFreeAccessLabel'),
      successTitle: t('freeAccessRequestReceived'),
      successBody: t('freeAccessRequestBody'),
    },
    standard: {
      eyebrow: t('standardAccessEyebrow'),
      title: t('requestStandardAccessTitle'),
      description: t('standardAccessDesc'),
      submitLabel: t('requestStandardAccessLabel'),
      successTitle: t('standardAccessRequestReceived'),
      successBody: t('standardAccessRequestBody'),
    },
    premium: {
      eyebrow: t('premiumAccessEyebrow'),
      title: t('discussPremiumAccessTitle'),
      description: t('premiumAccessDesc'),
      submitLabel: t('discussPremiumAccessLabel'),
      successTitle: t('premiumInquiryReceived'),
      successBody: t('premiumInquiryBody'),
    },
  })[selectedPlan], [t, selectedPlan]);

  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [requestedCountry, setRequestedCountry] = useState<CountryCode>('rwanda');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateEmail(email)) {
      setError(tErr('enterValidEmail'));
      setSuccess(false);
      return;
    }

    if (!companyName.trim()) {
      setError(tErr('enterInstitutionName'));
      setSuccess(false);
      return;
    }

    if (!contactName.trim()) {
      setError(tErr('enterContactName'));
      setSuccess(false);
      return;
    }

    setError(null);
    setSuccess(true);
  };

  const onGoogleSignup = async () => {
    setError(null);
    setIsGoogleSubmitting(true);
    try {
      await signInWithGoogleFree({
        requestedCountry,
        companyName: companyName.trim() || undefined,
        contactName: contactName.trim() || undefined,
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : tErr('googleSignupFailed');
      setError(message);
    } finally {
      setIsGoogleSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-6 py-12">
      <div className="w-full max-w-2xl rounded-2xl border border-[#E2E8F0] bg-white p-10 shadow-sm">
        <div className="mb-8 text-center">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#E10613]">{planCopy.eyebrow}</div>
          <h1 className="text-3xl font-black text-[#0F172A]">{planCopy.title}</h1>
          <p className="mt-2 text-sm text-[#64748B]">{planCopy.description}</p>
        </div>

        {success ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
            <p className="font-semibold text-emerald-900">{planCopy.successTitle}</p>
            <p className="mt-2">{planCopy.successBody}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/demo')}
                className="rounded-lg bg-[#E10613] px-4 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#C1121F]"
              >
                {t('viewSampleInsights')}
              </button>
              <button
                onClick={() => navigate('/login')}
                className="rounded-lg border border-[#E2E8F0] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] hover:border-[#CBD5E1]"
              >
                {t('signIn')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            {selectedPlan === 'free' && (
              <>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#64748B]">{t('countryAccess')}</label>
                  <select
                    value={requestedCountry}
                    onChange={(event) => setRequestedCountry(event.target.value as CountryCode)}
                    className="h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-[#0F172A] outline-none transition-colors focus:border-[#E10613]"
                  >
                    {COUNTRY_CHOICES.map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {choice.label.en}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-4 text-sm text-[#344054]">
                  {t('freeAccessNote')}
                </div>

                <button
                  type="button"
                  onClick={onGoogleSignup}
                  disabled={isGoogleSubmitting}
                  className="h-12 w-full rounded-lg border border-[#E2E8F0] bg-white font-black text-[#0F172A] transition-colors hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isGoogleSubmitting ? t('connectingGoogle') : t('continueWithGoogle')}
                </button>

                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#CBD5E1]">
                  <div className="h-px flex-1 bg-[#E2E8F0]" />
                  <span>or</span>
                  <div className="h-px flex-1 bg-[#E2E8F0]" />
                </div>
              </>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#64748B]">{t('workEmail')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-[#0F172A] outline-none transition-colors placeholder:text-[#CBD5E1] focus:border-[#E10613]"
                  placeholder="name@bank.com"
                />
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#64748B]">{t('primaryContact')}</label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(event) => setContactName(event.target.value)}
                  className="h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-[#0F172A] outline-none transition-colors placeholder:text-[#CBD5E1] focus:border-[#E10613]"
                  placeholder="Full name"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#64748B]">{t('institution')}</label>
              <input
                type="text"
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                className="h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-[#0F172A] outline-none transition-colors placeholder:text-[#CBD5E1] focus:border-[#E10613]"
                placeholder="Bank name"
              />
            </div>

            <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-4 text-sm text-[#475569]">
              {selectedPlan === 'free' ? (
                <p>{t('freeAccessNote')}</p>
              ) : (
                <p>{t('standardPremiumNote')}</p>
              )}
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="h-12 w-full rounded-lg bg-[#E10613] font-black text-white transition-colors hover:bg-[#C1121F]"
            >
              {planCopy.submitLabel}
            </button>

            <div className="flex items-center justify-between text-xs text-[#64748B]">
              <Link to="/" className="underline underline-offset-4 hover:text-[#0F172A]">
                {t('backToHomepage')}
              </Link>
              <Link to="/login" className="underline underline-offset-4 hover:text-[#0F172A]">
                {t('returningUser')}
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Signup;
